/**
 * Consultation / PDF gate for the India BRSR Readiness Checker.
 * Posts contact + questionnaire + results + PDF to /api/assessment-lead.
 */
import { blobToBase64 } from '../lead-capture/adapter'
import type { BrsrResult, BrsrState } from '../../data/brsr/types'

export const BRSR_LEAD_ENDPOINT = '/api/assessment-lead'

export const BRSR_LEAD_SOURCE = 'india-brsr-readiness'

export const BRSR_LEAD_FORM_TYPE = 'brsr-readiness-lead'

export interface BrsrLeadInput {
  fullName: string
  workEmail: string
  companyName: string
  jobTitle: string
  phone?: string
  consentContact: boolean
  consentMarketing: boolean
  qualificationSummary: string
  page?: string
  state: BrsrState
  result: BrsrResult
  pdfBlob: Blob
  pdfFileName: string
}

export type BrsrLeadResult =
  | { ok: true; delivered: boolean; message: string; leadId?: string }
  | { ok: false; configured: boolean; message: string }

export async function submitBrsrLead(input: BrsrLeadInput): Promise<BrsrLeadResult> {
  try {
    const pdfBase64 = await blobToBase64(input.pdfBlob)
    const res = await fetch(BRSR_LEAD_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fullName: input.fullName,
        workEmail: input.workEmail,
        email: input.workEmail,
        companyName: input.companyName,
        company: input.companyName,
        jobTitle: input.jobTitle,
        phone: input.phone || '',
        consentReport: input.consentContact ? 'yes' : 'no',
        consentMarketing: input.consentMarketing ? 'yes' : 'no',
        qualificationSummary: input.qualificationSummary.slice(0, 500),
        methodologyVersion: 'brsr-1.0.0',
        source: BRSR_LEAD_SOURCE,
        formType: BRSR_LEAD_FORM_TYPE,
        page: input.page || '/tools/india-brsr-readiness/results',
        answers: {
          responses: input.state.responses,
          currentStep: input.state.currentStep,
          methodologyVersion: input.state.methodologyVersion,
          regulatoryConfigVersion: input.state.regulatoryConfigVersion,
        },
        results: input.result,
        pdfBase64,
        pdfFileName: input.pdfFileName,
      }),
    })
    const data = (await res.json().catch(() => ({}))) as {
      error?: string
      ok?: boolean
      localDev?: boolean
      leadId?: string
    }
    if (res.status === 503 || data.error === 'Lead service not configured') {
      return {
        ok: false,
        configured: false,
        message: 'Lead storage is not configured. Your details were not saved. You can still download the PDF.',
      }
    }
    if (!res.ok) {
      return {
        ok: false,
        configured: true,
        message: data.error || 'The request could not be sent. Please check the form and try again.',
      }
    }
    if (data.localDev) {
      return {
        ok: true,
        delivered: false,
        leadId: data.leadId,
        message:
          'Details were accepted. Email notification may be off in this environment; the CRM record is stored when Supabase is configured.',
      }
    }
    return {
      ok: true,
      delivered: true,
      leadId: data.leadId,
      message: 'Your request was saved. EcopsHub will follow up if a conversation is scheduled. This is not a compliance review.',
    }
  } catch {
    return {
      ok: false,
      configured: true,
      message: 'Network error. The request was not sent. You can still download the PDF.',
    }
  }
}
